import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { LoginView } from '../components/LoginView.js';
import { buildAuthorizeRedirect, isTrustedLoginIssuer } from '../lib/auth.js';

type LoginSearch = {
  /** OIDC Core §4 third-party-initiated login (the Okta dashboard tile). */
  readonly iss?: string | undefined;
};

export const Route = createFileRoute('/login')({
  component: LoginRoute,
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    iss: typeof search['iss'] === 'string' ? search['iss'] : undefined,
  }),
});

function LoginRoute() {
  const { iss } = Route.useSearch();
  return <LoginRouteContent iss={iss} />;
}

export type LoginRouteContentProps = {
  readonly iss?: string | undefined;
};

/**
 * Exported for tests (the route component needs router context). A TRUSTED
 * `iss` (Okta-initiated login) auto-starts the same Hosted-UI redirect the
 * button performs — an untrusted or absent one falls back to the manual
 * sign-in action (fail closed, `isTrustedLoginIssuer`). The ref pins the
 * auto-start to ONE redirect under StrictMode's double-invoked effects.
 */
export function LoginRouteContent({ iss }: LoginRouteContentProps) {
  const [error, setError] = useState<string | undefined>();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const autoStarted = useRef(false);

  const signIn = useCallback(async () => {
    setError(undefined);
    setIsRedirecting(true);
    try {
      window.location.assign(await buildAuthorizeRedirect());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not start sign-in.');
      setIsRedirecting(false);
    }
  }, []);

  useEffect(() => {
    if (autoStarted.current || iss === undefined || !isTrustedLoginIssuer(iss)) return;
    autoStarted.current = true;
    void signIn();
  }, [iss, signIn]);

  return (
    <LoginView
      {...(error === undefined ? {} : { error })}
      isRedirecting={isRedirecting}
      onSignIn={() => void signIn()}
    />
  );
}
